"use client"

import { ProductBySku } from "@lib/data/products"
import { convertToLocale } from "@lib/util/money"
import NativeSelect from "@modules/common/components/native-select"
import { Text } from "@modules/common/components/ui"

type RequiredFittingsProps = {
  fittings: ProductBySku[]
  inletId: string
  outletId: string
  onInletChange: (variantId: string) => void
  onOutletChange: (variantId: string) => void
  disabled?: boolean
}

const fittingLabel = (fitting: ProductBySku) => {
  if (fitting.price == null || !fitting.currency_code) {
    return fitting.title
  }

  return `${fitting.title} — ${convertToLocale({
    amount: fitting.price,
    currency_code: fitting.currency_code,
  })}`
}

// ORB-port radiators cannot connect coolant hoses without adapter fittings.
// Forces an inlet and an outlet pick (may be the same fitting twice) before
// the parent enables add-to-cart. Only in-stock fittings are offered.
const RequiredFittings = ({
  fittings,
  inletId,
  outletId,
  onInletChange,
  onOutletChange,
  disabled,
}: RequiredFittingsProps) => {
  return (
    <div
      className="flex flex-col gap-y-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-3"
      data-testid="required-fittings"
    >
      <Text className="text-small-regular font-semibold text-amber-900">
        Required fittings
      </Text>
      <Text className="text-small-regular text-amber-800">
        This radiator has ORB ports &mdash; it needs an inlet and an outlet
        fitting to connect your hoses.
      </Text>
      <div className="flex flex-col gap-y-1">
        <label className="text-sm text-amber-900" htmlFor="inlet-fitting">
          Inlet fitting
        </label>
        <NativeSelect
          id="inlet-fitting"
          placeholder="Select inlet fitting"
          value={inletId}
          onChange={(e) => onInletChange(e.target.value)}
          disabled={disabled}
          className="bg-white"
          data-testid="inlet-fitting-select"
        >
          {fittings.map((fitting) => (
            <option key={fitting.variant_id} value={fitting.variant_id}>
              {fittingLabel(fitting)}
            </option>
          ))}
        </NativeSelect>
      </div>
      <div className="flex flex-col gap-y-1">
        <label className="text-sm text-amber-900" htmlFor="outlet-fitting">
          Outlet fitting
        </label>
        <NativeSelect
          id="outlet-fitting"
          placeholder="Select outlet fitting"
          value={outletId}
          onChange={(e) => onOutletChange(e.target.value)}
          disabled={disabled}
          className="bg-white"
          data-testid="outlet-fitting-select"
        >
          {fittings.map((fitting) => (
            <option key={fitting.variant_id} value={fitting.variant_id}>
              {fittingLabel(fitting)}
            </option>
          ))}
        </NativeSelect>
      </div>
    </div>
  )
}

export default RequiredFittings
